import { Box, CircularProgress, Divider, Typography, Grid2 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { log } from '@/logger/logger.ts';
import { Input } from '@/UI/Input/Input';
import { FlexCenHorVer, FlexCol, FlexColCenVer } from '@/UI/styles';
import { WorkspaceIcon } from '@/components/Dashboard/UserMenu/WorkspaceIcon';
import { I18N_KEYS } from '@/language/keys';
import { PermissionsContainer } from '@/components/PermissionsContainer/PermissionsContainer';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { SubscriptionType } from '@/types/shared';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { openIntercom } from '@/providers/intercom';
import { getAxiosInstance } from '@/services/axiosConfig';
import useWorkspacesStore from '@/state/workspaces.state';
import { useUserStore } from '@/state/user.state';
import { PlanSection } from './PlanSection';
import { CreditsSection } from './CreditsSection';
import { CreditsAllocationSection } from './CreditsAllocationSection';

const logger = log.getLogger('WorkspaceSettings');
const axiosInstance = getAxiosInstance();

export const WorkspaceSettings = () => {
  const { t } = useTranslation();
  const { isSubscriptionTypeIn, isWorkspaceAdmin } = useSubscriptionPermissions();

  const workspaceId = useWorkspacesStore((state) => state.activeWorkspace.workspaceId);
  const workspaceName = useWorkspacesStore((state) => state.activeWorkspace.workspaceName);
  const updateActiveWorkspace = useWorkspacesStore((state) => state.updateActiveWorkspace);
  const updateUserWorkspaces = useWorkspacesStore((state) => state.updateUserWorkspaces);
  const activeWorkspace = useUserStore((state) => state.user?.activeWorkspace);
  const subscription = activeWorkspace?.subscription;

  const getContactUsCta = () => {
    if (subscription?.type === SubscriptionType.Team) {
      return (
        <Box sx={{ pb: 4 }}>
          <Typography variant="body-sm-rg">
            {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CONTACT_US_PART_1)}&nbsp;
          </Typography>
          <ButtonTextPlain onClick={openIntercom} variant="body-sm-rg">
            {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CONTACT_US_PART_2)}
          </ButtonTextPlain>
        </Box>
      );
    }
  };

  const onWorkspaceNameChange = async (name: string) => {
    if (!name || name === workspaceName) {
      return;
    }

    try {
      await axiosInstance.put(`/v1/workspaces/${workspaceId}`, { name });
      updateActiveWorkspace({ workspaceName: name });
      updateUserWorkspaces(workspaceId, { workspaceName: name });
    } catch (e) {
      logger.error('Error updating workspace name', e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  if (!subscription) {
    return (
      <FlexCenHorVer>
        <CircularProgress />
      </FlexCenHorVer>
    );
  }

  return (
    <FlexCol sx={{ width: 750, gap: 4 }}>
      <Grid2 container display="grid" direction="row" gridTemplateColumns="1fr 1.5fr 1fr" gap={4}>
        <Typography variant="body-lg-md">{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.FORM.GENERAL)}</Typography>
        <FlexColCenVer sx={{ gap: 2 }}>
          <WorkspaceIcon text={workspaceName} size="large" />
          <Input
            label={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.FORM.NAME)}
            sx={{ width: 240 }}
            defaultValue={workspaceName}
            onBlur={(e) => void onWorkspaceNameChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!isWorkspaceAdmin}
          />
        </FlexColCenVer>
      </Grid2>
      <PermissionsContainer permission="credits">
        <Divider />
        <CreditsSection />
        {isSubscriptionTypeIn([SubscriptionType.Team, SubscriptionType.Enterprise]) ? (
          <>
            <Divider />
            <CreditsAllocationSection />
          </>
        ) : null}
      </PermissionsContainer>
      <Divider />
      <PermissionsContainer permission="plan">
        <PlanSection />
      </PermissionsContainer>
      <Divider />
      {getContactUsCta()}
    </FlexCol>
  );
};
